import { ArrowIcon } from '../atoms/Icons/ArrowIcon'
import { createPathWithLang } from '../../utils/languagePathUtils'
import { NetworkConstants } from '../../utils/NetworkConstants'
import { goBack, historyState } from '../../utils/history'
export interface LocationState {
  prevPath: string
  report_id?: string
}

export enum BackLinkLabels {
  Assessments = 'Back to Assessments',
  Teams = 'Back to Teams',
  Employee_Report = 'Back to the Report',
  Team_Report = `Back to the Team's report`,
  Finder = 'Back to the Finder',
  Completed_Reviews = 'Back to Completed Reviews',
  Admin_Dashboard = 'Back to Admin Dashboard',
}

export const BackLink = (): JSX.Element => {
  // /admin/completed-reviews is admin-only and reachable only from the admin dashboard, so
  // when prevPath is unhelpful (empty, default route, or self-referential — which happens after
  // a goBack from a child page in a tab opened with target="_blank"), fall back to the dashboard.
  const isCompletedReviewsWithoutUsefulPrev = (): boolean => {
    const completedReviewsPath = createPathWithLang(NetworkConstants.URL_ADMIN_COMPLETED_REVIEWS)
    const onCompletedReviews = globalThis.location.pathname.includes(completedReviewsPath)
    if (!onCompletedReviews) return false
    const prev = historyState.prevPath
    if (!prev) return true
    if (prev.includes(completedReviewsPath)) return true
    if (prev === createPathWithLang(NetworkConstants.URL_REVIEW_LIST)) return true
    return false
  }

  // Ordered list of (path-fragment → label) pairs. First match wins.
  // The fragments for team/employee reports strip the dynamic `:id` segments so we match
  // against the static prefix.
  const labelMatchers: Array<[string, BackLinkLabels]> = [
    [createPathWithLang(NetworkConstants.URL_ADMIN_COMPLETED_REVIEWS), BackLinkLabels.Completed_Reviews],
    [createPathWithLang(NetworkConstants.URL_ADMIN_DASHBOARD), BackLinkLabels.Admin_Dashboard],
    [createPathWithLang(NetworkConstants.URL_TEAMS_DASHBOARD), BackLinkLabels.Teams],
    [createPathWithLang(NetworkConstants.URL_REVIEW_LIST), BackLinkLabels.Assessments],
    [createPathWithLang(NetworkConstants.URL_FIND_RESOURCES), BackLinkLabels.Finder],
    [createPathWithLang(NetworkConstants.URL_TEAM_SUMMARY.replace('/:teamId', '')), BackLinkLabels.Team_Report],
    [createPathWithLang(NetworkConstants.URL_REPORT.replace('/:employeeId', '')), BackLinkLabels.Employee_Report],
    [createPathWithLang(NetworkConstants.URL_NEW_REVIEW.replace('/:employeeId', '')), BackLinkLabels.Employee_Report],
  ]

  function getLabelFromPath(): string {
    if (isCompletedReviewsWithoutUsefulPrev()) return BackLinkLabels.Admin_Dashboard
    const path = historyState.prevPath
    if (!path) return BackLinkLabels.Assessments
    const match = labelMatchers.find(([fragment]) => path.includes(fragment))
    return match ? match[1] : BackLinkLabels.Assessments
  }

  const getForcedPath = (): string => {
    if (isCompletedReviewsWithoutUsefulPrev()) return createPathWithLang(NetworkConstants.URL_ADMIN_DASHBOARD)
    const currentPath = historyState.stack.at(-1)
    if (currentPath) {
      // for the /thanks/ path, force the back link to go to the review list, otherwise it goes
      // to the review, which takes you back to /thanks/, rendering the backlink button useless
      if (currentPath.includes(createPathWithLang(NetworkConstants.URL_REVIEW_FINISHED_MSG)))
        return createPathWithLang(NetworkConstants.URL_REVIEW_LIST)
    }
    return ''
  }

  return (
    <div className={`flex justify-between`}>
      <div className="flex flex-nowrap">
        <button
          className="text-stone-gray hover:text-stone-gray hover:underline text-2xl font-medium flex items-center"
          onClick={() => goBack(getForcedPath())}
        >
          <div className="mr-3">
            <ArrowIcon height={14} width={14} />
          </div>
          {getLabelFromPath()}
        </button>
      </div>
    </div>
  )
}
